import { cn } from '@/lib/utils'
import {
    BoldIcon,
    CodeIcon,
    HeadingIcon,
    ImageIcon,
    ImageUpIcon,
    ItalicIcon,
    LinkIcon,
    ListIcon,
    ListOrderedIcon,
    ListTodoIcon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import IconButton from '../Button/IconButton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Tooltip } from '../ui/tooltip'
import { IconPicker } from './IconPicker'
import { MarkdownView } from './MarkdownView'
import {
    alternarPrefixo,
    inserirNoTexto,
    tokenImagem,
    tokenLucideIcon,
} from './markdown-tokens'

/** Prefixos de bloco da barra: como marcar a linha e como reconhecê-la marcada. */
const CABECALHO = { montar: () => '### ', padrao: /^#{1,6} / }
const MARCADOR = { montar: () => '- ', padrao: /^- (?!\[[ x]\] )/ }
const NUMERO = {
    montar: (ordem: number) => `${ordem + 1}. `,
    padrao: /^\d+\. /,
}
const TAREFA = { montar: () => '- [ ] ', padrao: /^- \[[ x]\] / }

interface IProps {
    value: string
    onChange: (valor: string) => void
    /**
     * Guarda os arquivos e devolve o índice de cada imagem aceita, na ordem em
     * que entraram na lista — é o índice que o token do corpo referencia.
     */
    onAnexarImagens?: (arquivos: File[]) => Promise<string[]>
    /** Resolve o índice do token para exibir a imagem na pré-visualização. */
    resolverImagem?: (imagemId: string) => string | undefined
    placeholder?: string
    className?: string
}

export function MarkdownEditor({
    value,
    onChange,
    onAnexarImagens,
    resolverImagem,
    placeholder,
    className,
}: IProps) {
    const textarea = useRef<HTMLTextAreaElement>(null)
    const seletor = useRef<HTMLInputElement>(null)
    const [arrastando, setArrastando] = useState(false)

    // A leitura dos arquivos é assíncrona e o usuário pode continuar digitando
    // nesse meio tempo; montar o texto novo a partir da prop capturada na
    // chamada apagaria o que ele escreveu enquanto a imagem carregava.
    const valorAtual = useRef(value)
    valorAtual.current = value

    function aplicar(abertura: string, fechamento = '') {
        const campo = textarea.current
        if (!campo) return

        const { texto, cursor } = inserirNoTexto(
            value,
            abertura,
            campo.selectionStart,
            campo.selectionEnd,
            fechamento,
        )

        onChange(texto)

        requestAnimationFrame(() => {
            campo.focus()
            campo.setSelectionRange(cursor, cursor)
        })
    }

    function prefixar({
        montar,
        padrao,
    }: {
        montar: (ordem: number) => string
        padrao: RegExp
    }) {
        const campo = textarea.current
        if (!campo) return

        const { texto, inicio, fim } = alternarPrefixo(
            value,
            montar,
            padrao,
            campo.selectionStart,
            campo.selectionEnd,
        )

        onChange(texto)

        requestAnimationFrame(() => {
            campo.focus()
            campo.setSelectionRange(inicio, fim)
        })
    }

    async function anexar(arquivos: File[]) {
        if (!onAnexarImagens || !arquivos.length) return

        const campo = textarea.current
        const inicio = campo?.selectionStart ?? valorAtual.current.length
        const fim = campo?.selectionEnd ?? inicio

        const ids = await onAnexarImagens(arquivos)

        if (!ids.length) return

        const { texto, cursor } = inserirNoTexto(
            valorAtual.current,
            ids.map(id => `![imagem](${tokenImagem(id)})`).join('\n'),
            inicio,
            fim,
        )

        onChange(texto)

        requestAnimationFrame(() => {
            campo?.focus()
            campo?.setSelectionRange(cursor, cursor)
        })
    }

    const botoes = [
        {
            titulo: 'Cabeçalho',
            Icone: HeadingIcon,
            acao: () => prefixar(CABECALHO),
        },
        { titulo: 'Negrito', Icone: BoldIcon, acao: () => aplicar('**', '**') },
        { titulo: 'Itálico', Icone: ItalicIcon, acao: () => aplicar('_', '_') },
        {
            titulo: 'Link',
            Icone: LinkIcon,
            acao: () => aplicar('[', '](https://)'),
        },
        { titulo: 'Lista', Icone: ListIcon, acao: () => prefixar(MARCADOR) },
        {
            titulo: 'Lista numerada',
            Icone: ListOrderedIcon,
            acao: () => prefixar(NUMERO),
        },
        {
            titulo: 'Lista de tarefas',
            Icone: ListTodoIcon,
            acao: () => prefixar(TAREFA),
        },
        { titulo: 'Código', Icone: CodeIcon, acao: () => aplicar('`', '`') },
    ]

    const onDragAndDropEvents = {
        onDragOver: (event: React.DragEvent<unknown>) => {
            if (!onAnexarImagens) return

            event.preventDefault()
            setArrastando(true)
        },
        onDragLeave: (event: React.DragEvent<unknown>) => {
            if (
                Array.isArray(event.currentTarget) &&
                (event.currentTarget as any).contains(
                    event.relatedTarget as Node,
                )
            ) {
                return
            }

            setArrastando(false)
        },
        onDrop: (event: React.DragEvent<unknown>) => {
            if (!onAnexarImagens) return

            event.preventDefault()
            setArrastando(false)
            void anexar(Array.from(event.dataTransfer.files))
        },
    }

    return (
        <Tabs
            defaultValue="escrever"
            className={cn(
                'rounded-md border relative',
                arrastando && 'border-primary',
                className,
            )}
        >
            <div className="flex items-center justify-between border-b px-2 py-1">
                <TabsList className="flex gap-1">
                    <TabsTrigger
                        value="escrever"
                        className={cn(
                            'rounded-t-md px-3 py-1 text-sm',
                            'data-[state=active]:bg-card data-[state=active]:font-semibold',
                        )}
                    >
                        Escrever
                    </TabsTrigger>
                    <TabsTrigger
                        value="previsualizar"
                        className={cn(
                            'rounded-t-md px-3 py-1 text-sm',
                            'data-[state=active]:bg-card data-[state=active]:font-semibold',
                        )}
                    >
                        Pré-visualizar
                    </TabsTrigger>
                </TabsList>

                <div className="flex items-center [&_svg]:size-5!">
                    {botoes.map(({ titulo, Icone, acao }) => (
                        <Tooltip key={titulo} title={titulo}>
                            <IconButton key={titulo} onClick={acao}>
                                <Icone />
                            </IconButton>
                        </Tooltip>
                    ))}
                    <IconPicker
                        onSelect={nome => aplicar(tokenLucideIcon(nome))}
                    />
                    <Tooltip title="Inserir imagem — também aceita arrastar ou colar">
                        <IconButton
                            disabled={!onAnexarImagens}
                            onClick={() => seletor.current?.click()}
                        >
                            <ImageIcon />
                        </IconButton>
                    </Tooltip>
                    <input
                        ref={seletor}
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={event => {
                            void anexar(Array.from(event.target.files ?? []))
                            event.target.value = ''
                        }}
                    />
                </div>
            </div>

            <TabsContent value="escrever">
                <textarea
                    ref={textarea}
                    value={value}
                    placeholder={placeholder}
                    onChange={event => onChange(event.target.value)}
                    onPaste={event => {
                        const arquivos = Array.from(
                            event.clipboardData.files,
                        ).filter(arquivo => arquivo.type.startsWith('image/'))

                        // Só sequestra o paste quando há imagem: texto copiado
                        // junto com o arquivo continua caindo no textarea.
                        if (!arquivos.length) return

                        event.preventDefault()
                        void anexar(arquivos)
                    }}
                    className={cn(
                        'min-h-48 w-full',
                        'resize-y bg-transparent',
                        'p-3 text-sm outline-none',
                        'border-none text-foreground',
                    )}
                    {...onDragAndDropEvents}
                />
                <div
                    className={cn(
                        'absolute inset-0 flex flex-col',
                        'bg-accent opacity-0 gap-2',
                        'pointer-events-none rounded-md',
                        'transition-all duration-200',
                        'items-center justify-center',
                        {
                            'opacity-75': arrastando && !!onAnexarImagens,
                        },
                    )}
                >
                    <ImageUpIcon className="size-12" />
                    <span className="text-xl">Solte para inserir</span>
                </div>
            </TabsContent>

            <TabsContent value="previsualizar" className="min-h-48 p-3">
                <MarkdownView resolverImagem={resolverImagem}>
                    {value}
                </MarkdownView>
            </TabsContent>
        </Tabs>
    )
}
